/**
 * Reusable Levi Working Environment — pure state module.
 *
 * This module is intentionally free of React, DOM, storage, and I/O
 * imports. It exports:
 *
 *   1. LeviState        — the seven product states that describe where
 *                          Levi is in the contractor-facing workflow.
 *   2. deriveLeviState  — a pure function that computes the current
 *                          state from an adapter-produced view model.
 *
 * The derivation reads only adapter-produced booleans on
 * `viewModel.flags`. It does NOT read backend contract keys directly
 * (the seven-key recommendation contract and the four-key reasoning
 * contract are consumed only inside the page-owned view-model adapter
 * and inside the locked LeviVisionPanel). This keeps the working
 * environment strictly presentation-oriented and prevents any
 * accidental duplicate renderer of the locked contracts.
 *
 * Product state is a pure function of the view model and is
 * evaluated on every render — the working environment owns no
 * reducer for state itself.
 *
 * Precedence (top wins):
 *   1. analyzing              → REVIEWING
 *   2. analyzeError           → RECOVERABLE_ERROR
 *   3. hasRecommendation:
 *        a. hasPendingFollowUp        → NEEDS_INFORMATION
 *        b. fullTradeUnacknowledged   → WAITING_FOR_CONTRACTOR
 *        c. otherwise                 → RECOMMENDATION_READY
 *   4. hasContractorInput     → LISTENING
 *   5. otherwise              → READY
 */

export const LeviState = Object.freeze({
  READY: "ready",
  LISTENING: "listening",
  REVIEWING: "reviewing",
  NEEDS_INFORMATION: "needs_information",
  WAITING_FOR_CONTRACTOR: "waiting_for_contractor",
  RECOMMENDATION_READY: "recommendation_ready",
  RECOVERABLE_ERROR: "recoverable_error",
});

const _flag = (flags, key) => Boolean(flags && flags[key]);

export const deriveLeviState = (viewModel) => {
  const flags = (viewModel && viewModel.flags) || {};

  if (_flag(flags, "analyzing")) return LeviState.REVIEWING;
  if (_flag(flags, "analyzeError")) return LeviState.RECOVERABLE_ERROR;

  if (_flag(flags, "hasRecommendation")) {
    if (_flag(flags, "hasPendingFollowUp")) return LeviState.NEEDS_INFORMATION;
    if (_flag(flags, "fullTradeUnacknowledged")) return LeviState.WAITING_FOR_CONTRACTOR;
    return LeviState.RECOMMENDATION_READY;
  }

  if (_flag(flags, "hasContractorInput")) return LeviState.LISTENING;
  return LeviState.READY;
};

/**
 * Status caption key resolution — maps a LeviState value to the
 * onboarding_i18n key under `levi_estimate.*` that the working
 * environment should display for the current state. Kept out of the
 * component so the mapping is verifiable in isolation.
 */
export const LEVI_STATUS_CAPTION_KEYS = Object.freeze({
  [LeviState.READY]: "levi_estimate.working_env_status_ready",
  [LeviState.LISTENING]: "levi_estimate.working_env_status_listening",
  [LeviState.REVIEWING]: "levi_estimate.working_env_status_reviewing",
  [LeviState.NEEDS_INFORMATION]: "levi_estimate.working_env_status_needs_information",
  [LeviState.WAITING_FOR_CONTRACTOR]: "levi_estimate.working_env_status_waiting_for_contractor",
  [LeviState.RECOMMENDATION_READY]: "levi_estimate.working_env_status_recommendation_ready",
  [LeviState.RECOVERABLE_ERROR]: "levi_estimate.working_env_status_recoverable_error",
});

export const statusCaptionKeyFor = (state) =>
  LEVI_STATUS_CAPTION_KEYS[state] || LEVI_STATUS_CAPTION_KEYS[LeviState.READY];
